import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState, type FormEvent } from "react";
import { getMyInvestor, updateMyInvestor } from "@/lib/investor.functions";

export const Route = createFileRoute("/investor/profile")({
  head: () => ({
    meta: [
      { title: "Profile - Investor - DeckScore" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: InvestorProfile,
});

function InvestorProfile() {
  const runGet = useServerFn(getMyInvestor);
  const runUpdate = useServerFn(updateMyInvestor);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState("vc");
  const [thesis, setThesis] = useState("");
  const [stage, setStage] = useState("");
  const [sector, setSector] = useState("");
  const [region, setRegion] = useState("");
  const [contact, setContact] = useState("");
  const [openToProgram, setOpenToProgram] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { investor } = await runGet();
        if (investor) {
          setName(investor.name ?? "");
          setType(investor.type ?? "vc");
          setThesis(investor.thesis ?? "");
          setStage((investor.stage_focus ?? []).join(", "));
          setSector((investor.sector_focus ?? []).join(", "));
          setRegion(investor.region ?? "");
          setContact(investor.contact_email ?? "");
          setOpenToProgram(!!(investor as { open_to_program?: boolean }).open_to_program);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [runGet]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    try {
      await runUpdate({
        data: {
          name: name.trim(),
          type,
          thesis: thesis.trim() || null,
          stage_focus: stage.split(",").map((x) => x.trim()).filter(Boolean),
          sector_focus: sector.split(",").map((x) => x.trim()).filter(Boolean),
          region: region.trim() || null,
          open_to_program: openToProgram,
        },
      });
      setSaved(true);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="text-ink-secondary">Loading…</p>;

  const inputCls =
    "min-h-[40px] w-full rounded-lg border border-line bg-bg px-3 text-[14px] text-ink placeholder:text-ink-tertiary focus:border-accent focus-visible:outline-none";

  return (
    <>
      <div className="mb-8">
        <p className="font-script text-accent text-[22px] leading-none">profile</p>
        <h1 className="font-display text-[28px] font-semibold mt-1">Investor profile</h1>
      </div>
      <form onSubmit={onSubmit} className="bg-surface border border-line rounded-2xl p-6 md:p-8 flex flex-col gap-4 max-w-2xl">
        <div>
          <label className="text-[13px] text-ink-secondary block mb-1">Firm / fund name</label>
          <input required className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className="text-[13px] text-ink-secondary block mb-1">Type</label>
          <select className={inputCls} value={type} onChange={(e) => setType(e.target.value)}>
            <option value="vc">VC</option>
            <option value="angel">Angel</option>
            <option value="accelerator">Accelerator</option>
            <option value="incubator">Incubator</option>
          </select>
        </div>
        <div>
          <label className="text-[13px] text-ink-secondary block mb-1">Thesis</label>
          <textarea rows={3} className={inputCls + " min-h-[80px]"} value={thesis} onChange={(e) => setThesis(e.target.value)} />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-[13px] text-ink-secondary block mb-1">Stage focus (comma-separated)</label>
            <input className={inputCls} value={stage} onChange={(e) => setStage(e.target.value)} placeholder="pre-seed, seed" />
          </div>
          <div>
            <label className="text-[13px] text-ink-secondary block mb-1">Sector focus (comma-separated)</label>
            <input className={inputCls} value={sector} onChange={(e) => setSector(e.target.value)} placeholder="AI, fintech" />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-[13px] text-ink-secondary block mb-1">Region</label>
            <input className={inputCls} value={region} onChange={(e) => setRegion(e.target.value)} />
          </div>
          <div>
            <label className="text-[13px] text-ink-secondary block mb-1">Verified email (referrals go here)</label>
            <input type="email" readOnly disabled className={inputCls + " opacity-70 cursor-not-allowed"} value={contact} />
            <p className="text-[12px] text-ink-tertiary mt-1">Your signed-in email - always the delivery address.</p>
          </div>
        </div>
        <label className="flex items-start gap-3 rounded-xl border border-line bg-bg p-3.5 cursor-pointer mt-1">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-accent"
            checked={openToProgram}
            onChange={(e) => setOpenToProgram(e.target.checked)}
          />
          <span className="text-[13px] text-ink leading-snug">
            <b className="font-semibold">Optional:</b> I'm open to DeckScore matching me with relevant founders. Only after you review and approve me - never automatically.
            <span className="block text-[12px] text-ink-tertiary mt-1">
              Leave this off and you'll only ever receive decks from founders you personally share your page with.
            </span>
          </span>
        </label>
        <div className="flex items-center gap-3 mt-2">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex rounded-full bg-accent text-accent-ink px-5 py-2 text-[14px] font-bold disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save profile"}
          </button>
          {saved && <span className="text-[13px] text-[#1F7A3C]">Saved ✓</span>}
        </div>
      </form>
    </>
  );
}
